import { SaveAlt, ClearOutlined, EditOutlined, DeleteOutlined } from '@mui/icons-material';
import {
  GridActionsCellItem,
  DataGridProProps,
  GridRowModesModel,
  GridRowModes,
  GridRowId,
  GridApi,
} from '@mui/x-data-grid-pro';
import { useState, useMemo, MutableRefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { CmdiRow } from './CmdiDataGridWidget.types';

type UseCmdiDataGridActionsProps = Pick<DataGridProProps<CmdiRow>, 'columns'> & {
  apiRef: MutableRefObject<GridApi>;
  onAdd: () => CmdiRow;
  onDelete: (id: string) => void;
  onCancel: (id: string) => void;
  onCustomAdd?: () => void;
  onCustomEdit?: (id: string) => void;
};

type UseCmdiDataGridActionsResult = Pick<
  DataGridProProps<CmdiRow>,
  'columns' | 'rowModesModel' | 'onRowModesModelChange'
> & {
  onRowAdd: () => void;
};

export const useCmdiDataGridActions = ({
  apiRef,
  columns: columnsProp,
  onDelete,
  onAdd,
  onCancel,
  onCustomAdd,
  onCustomEdit,
}: UseCmdiDataGridActionsProps): UseCmdiDataGridActionsResult => {
  const { t } = useTranslation(undefined, { keyPrefix: 'DataGrid.column' });
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});

  const columns = useMemo<DataGridProProps<CmdiRow>['columns']>(() => {
    const handleEditClick = (id: GridRowId) => () => {
      if (onCustomEdit) {
        onCustomEdit(`${id}`);
        return;
      }
      setRowModesModel((prevRowModesModel) => ({
        ...prevRowModesModel,
        [id]: { mode: GridRowModes.Edit },
      }));
    };
    const handleSaveClick = (id: GridRowId) => () => {
      setRowModesModel((prevRowModesModel) => ({
        ...prevRowModesModel,
        [id]: { mode: GridRowModes.View },
      }));
    };
    const handleDeleteClick = (id: GridRowId) => async () => {
      onDelete(`${id}`);
    };

    const handleCancelClick = (id: GridRowId) => () => {
      setRowModesModel((prevRowModesModel) => ({
        ...prevRowModesModel,
        [id]: { mode: GridRowModes.View, ignoreModifications: true },
      }));
      onCancel(`${id}`);
    };

    return [
      ...columnsProp,
      {
        field: 'actions',
        type: 'actions',
        headerName: t('actions'),
        width: 100,
        resizable: false,
        hideable: false,
        getActions: ({ id }: any) => {
          const isInEditMode = apiRef.current?.getRowMode(id) === GridRowModes.Edit;

          if (isInEditMode) {
            return [
              <GridActionsCellItem key="save" icon={<SaveAlt />} label="Save" onClick={handleSaveClick(id)} />,
              <GridActionsCellItem
                key="cancel"
                icon={<ClearOutlined />}
                label="Cancel"
                className="textPrimary"
                onClick={handleCancelClick(id)}
                color="inherit"
              />,
            ];
          }

          return [
            <GridActionsCellItem
              key="edit"
              icon={<EditOutlined />}
              label="Edit"
              className="textPrimary"
              onClick={handleEditClick(id)}
              color="inherit"
            />,
            <GridActionsCellItem
              key="del"
              icon={<DeleteOutlined />}
              label="Delete"
              onClick={handleDeleteClick(id)}
              color="inherit"
            />,
          ];
        },
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnsProp, t]);

  const addRow = () => {
    if (onCustomAdd) {
      onCustomAdd();
      return;
    }
    const newRow = onAdd();
    newRow.isNew = true;
    setRowModesModel((prevRowModesModel) => ({
      ...prevRowModesModel,
      [newRow.id]: { mode: GridRowModes.Edit },
    }));
  };

  return {
    columns,
    rowModesModel,
    onRowModesModelChange: setRowModesModel,
    onRowAdd: addRow,
  };
};
